import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Clock, Loader2, CalendarCheck, CheckCircle2, Lock, Stamp, Scale, BookHeart, FileText, HeartHandshake, Trash2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUCALYPTUS = "https://images.pexels.com/photos/8903651/pexels-photo-8903651.jpeg";

const ICONS = {
  legacy_planning: BookHeart, eol_planning: HeartHandshake, legacy_builder: Scale,
  notary: Stamp, document_review: FileText,
};
const BLURBS = {
  legacy_planning: "Map out your legacy goals and next steps.",
  eol_planning: "A thorough, compassionate end-of-life planning session.",
  legacy_builder: "Build your complete legacy with expert guidance.",
  notary: "Get documents notarized — available any time, open to all.",
  document_review: "Review and prepare your important documents together.",
};

const todayStr = () => new Date().toISOString().split("T")[0];
const _fmt12js = (min) => {
  const h = Math.floor(min / 60), m = min % 60;
  const ap = h < 12 ? "AM" : "PM";
  const hh = (h % 12) || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function Booking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [myBookings, setMyBookings] = useState([]);

  const loadMine = () => {
    if (!user) { setMyBookings([]); return; }
    axios.get(`${API}/booking/mine`).then(({ data }) => setMyBookings(data)).catch(() => {});
  };
  useEffect(() => { loadMine(); }, [user]); // eslint-disable-line

  const bookingDT = (b) => new Date(`${b.date}T${String(Math.floor(b.start_min / 60)).padStart(2, "0")}:${String(b.start_min % 60).padStart(2, "0")}:00`);
  const prettyWhen = (b) => bookingDT(b).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) + " · " + _fmt12js(b.start_min);
  const isUpcoming = (b) => bookingDT(b) > new Date();

  const cancelBooking = async (id) => {
    if (!window.confirm("Cancel this appointment? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/booking/${id}`);
      setMyBookings((p) => p.filter((b) => b.id !== id));
      toast.success("Appointment cancelled");
    } catch {
      toast.error("Could not cancel. Please try again.");
    }
  };

  useEffect(() => {
    axios.get(`${API}/booking/services`).then(({ data }) => setServices(data.services)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const consultLocked = (svc) => svc?.type === "consultation" && (!user || !user.is_paid);

  useEffect(() => {
    if (!service || consultLocked(service)) { setSlots([]); return; }
    setLoadingSlots(true);
    setSlot("");
    axios.get(`${API}/booking/slots`, { params: { service: service.id, date } })
      .then(({ data }) => setSlots(data.slots))
      .catch(() => toast.error("Could not load times."))
      .finally(() => setLoadingSlots(false));
  }, [service, date]); // eslint-disable-line

  const submit = async () => {
    if (!slot) { toast.error("Please pick a time."); return; }
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/booking`, { service: service.id, date, time: slot, ...form });
      setConfirmed(data);
      loadMine();
    } catch (err) {
      const code = err.response?.status;
      if (code === 401) { toast.error("Please sign in to book a consultation."); navigate("/login"); }
      else if (code === 402) { toast.error("A membership is required for consultations."); navigate("/pricing"); }
      else toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory text-charcoal relative overflow-hidden">
      <div className="eucalyptus-corner top-0 left-0" style={{ backgroundImage: `url(${EUCALYPTUS})`, transform: "scaleX(-1)" }} />
      <div className="eucalyptus-corner bottom-0 right-0" style={{ backgroundImage: `url(${EUCALYPTUS})` }} />

      <nav className="sticky top-0 z-50 backdrop-blur-md bg-ivory/80 border-b border-gold/20">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3" data-testid="booking-home">
            <img src={LOGO_URL} alt="The Ever Grace Group" className="h-12 w-12 rounded-full object-cover" />
            <span className="font-serif text-xl text-navy"><span>Ever</span> <span className="text-gold">Grace</span></span>
          </button>
          <Link to="/" className="flex items-center gap-1 text-sm text-navy/70 hover:text-navy"><ArrowLeft className="w-4 h-4" /> Home</Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-14 relative z-10">
        {confirmed ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gold/25 rounded-md p-12 text-center" data-testid="booking-confirmed">
            <CheckCircle2 className="w-14 h-14 text-sage mx-auto mb-4" />
            <h1 className="font-serif text-4xl text-navy mb-3">You're booked!</h1>
            <p className="text-charcoal/75 mb-6">
              <strong>{confirmed.service_name}</strong><br />
              {confirmed.date} at {confirmed.time} ({confirmed.duration} min, ET)
            </p>
            <p className="text-sm text-charcoal/60 mb-8">A confirmation has been emailed to {form.email}.</p>
            <Button onClick={() => { setConfirmed(null); setService(null); setSlot(""); }} data-testid="book-another-btn"
              className="bg-navy hover:bg-navy/90 text-ivory rounded-full px-8">Book another</Button>
          </motion.div>
        ) : (
          <>
            <div className="text-center mb-12">
              <p className="uppercase tracking-[0.3em] text-xs text-sage mb-3">Schedule with us</p>
              <h1 className="font-serif text-4xl md:text-6xl text-navy mb-4" data-testid="booking-title">Book a Consultation</h1>
              <p className="text-charcoal/75 text-lg max-w-xl mx-auto">
                Consultations run every day, 8am–10pm ET. Notary services are available any time.
              </p>
            </div>

            {/* My Appointments */}
            {user && myBookings.length > 0 && (
              <div className="mb-10 bg-white border border-gold/20 rounded-md p-6" data-testid="my-appointments">
                <div className="flex items-center gap-2 mb-4 text-navy">
                  <CalendarClock className="w-5 h-5 text-sage" />
                  <h2 className="font-serif text-2xl">My Appointments</h2>
                </div>
                <div className="space-y-3">
                  {myBookings.slice().sort((a, b) => bookingDT(a) - bookingDT(b)).map((b) => {
                    const upcoming = isUpcoming(b);
                    return (
                      <div key={b.id} data-testid={`appt-${b.id}`}
                        className="flex items-center gap-4 border border-gold/15 rounded-md p-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-serif text-lg text-navy leading-tight">{b.service_name}</p>
                          <p className="text-sm text-charcoal/70">{prettyWhen(b)} · {b.duration} min ET</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${upcoming ? "bg-sage/20 text-navy" : "bg-charcoal/10 text-charcoal/60"}`}>
                          {upcoming ? "Upcoming" : "Past"}
                        </span>
                        {upcoming && (
                          <Button variant="ghost" size="icon" onClick={() => cancelBooking(b.id)} data-testid={`cancel-appt-${b.id}`}
                            className="text-destructive hover:bg-destructive/5"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 1: service */}
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {services.map((s) => {
                const Icon = ICONS[s.id] || CalendarCheck;
                const active = service?.id === s.id;
                return (
                  <button key={s.id} onClick={() => setService(s)} data-testid={`service-${s.id}`}
                    className={`text-left rounded-md p-5 border transition-[border-color,box-shadow] ${
                      active ? "border-gold shadow-[0_12px_30px_rgba(196,162,78,0.18)] bg-white" : "border-gold/20 bg-white hover:border-gold/50"
                    }`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-10 h-10 rounded-full bg-sage/15 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-navy" strokeWidth={1.5} />
                      </span>
                      <span className="text-xs text-charcoal/60 flex items-center gap-1"><Clock className="w-3 h-3" />{s.minutes} min</span>
                      {s.type === "consultation" && <Lock className="w-3 h-3 text-gold ml-auto" />}
                    </div>
                    <h3 className="font-serif text-xl text-navy leading-tight">{s.name}</h3>
                    <p className="text-sm text-charcoal/70 mt-1">{BLURBS[s.id]}</p>
                  </button>
                );
              })}
            </div>

            {service && consultLocked(service) && (
              <div className="bg-gold/10 border border-gold/30 rounded-md p-6 text-center" data-testid="consult-locked">
                <Lock className="w-6 h-6 text-gold mx-auto mb-2" />
                <p className="text-navy mb-4">Consultations are available to members. Notary appointments are open to everyone.</p>
                <div className="flex gap-3 justify-center">
                  {!user
                    ? <Button onClick={() => navigate("/login")} data-testid="locked-signin" className="bg-navy text-ivory rounded-full px-6">Sign in</Button>
                    : <Button onClick={() => navigate("/pricing")} data-testid="locked-pricing" className="bg-gold text-navy rounded-full px-6">Become a member</Button>}
                </div>
              </div>
            )}

            {service && !consultLocked(service) && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                {/* Step 2: date + slots */}
                <div className="bg-white border border-gold/20 rounded-md p-6">
                  <Label className="text-navy font-semibold text-sm">Choose a date</Label>
                  <Input type="date" value={date} min={todayStr()} onChange={(e) => setDate(e.target.value)}
                    data-testid="booking-date" className="mt-2 bg-ivory border-gold/25 focus-visible:ring-sage max-w-xs" />
                  <div className="mt-5">
                    <Label className="text-navy font-semibold text-sm">Available times (ET)</Label>
                    {loadingSlots ? (
                      <div className="py-6"><Loader2 className="w-5 h-5 animate-spin text-sage" /></div>
                    ) : slots.length === 0 ? (
                      <p className="text-sm text-charcoal/60 py-4" data-testid="no-slots">No times available for this day. Try another date.</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2 max-h-56 overflow-y-auto planner-scroll">
                        {slots.map((s) => (
                          <button key={s.time} onClick={() => setSlot(s.time)} data-testid={`slot-${s.time}`}
                            className={`text-sm rounded-md py-2 border transition-colors ${
                              slot === s.time ? "bg-navy text-ivory border-navy" : "bg-ivory border-gold/25 text-navy hover:border-gold/60"
                            }`}>{s.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 3: details */}
                {slot && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-gold/20 rounded-md p-6 space-y-4">
                    <h3 className="font-serif text-2xl text-navy">Your details</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-navy text-sm">Full name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                          data-testid="booking-name" className="mt-1 bg-ivory border-gold/25 focus-visible:ring-sage" />
                      </div>
                      <div>
                        <Label className="text-navy text-sm">Email</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                          data-testid="booking-email" className="mt-1 bg-ivory border-gold/25 focus-visible:ring-sage" />
                      </div>
                      <div>
                        <Label className="text-navy text-sm">Phone (optional)</Label>
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          data-testid="booking-phone" className="mt-1 bg-ivory border-gold/25 focus-visible:ring-sage" />
                      </div>
                      <div>
                        <Label className="text-navy text-sm">Anything we should know? (optional)</Label>
                        <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          data-testid="booking-notes" className="mt-1 bg-ivory border-gold/25 focus-visible:ring-sage" />
                      </div>
                    </div>
                    <Button onClick={submit} disabled={submitting} data-testid="confirm-booking-btn"
                      className="bg-gold hover:bg-gold/90 text-navy rounded-full px-8 py-6 font-semibold group">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Confirm booking
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </motion.div>
                )}
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
